/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import React, { Fragment, useLayoutEffect, useRef, useState } from "react";

import { useShallow } from "zustand/react/shallow";
import { useFiltersHeightStore } from "@/stores/useFiltersHeightStore";
import { useMainBannerStore } from "@/stores/useMainBannerStore";
import { useTableHeadStore } from "@/stores/useTableHeadStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import { useVirtualizer } from "@tanstack/react-virtual";

import { IntegrationsRow } from "../../../../Pages/Integrations/types";
import { ROUTES, TableRowData } from "../../../../shared";
import useGeneralHeights from "../../../../shared/hooks/useGeneralHeights";
import { useTableStyles } from "../../styles/styles";
import MobileTableHead from "./components/MobileTableHead";
import MobileTableItem from "./components/MobileTableItem";

interface IMobileTableProps {
  route: ROUTES;
  hasDetails: boolean;
  toMd: boolean;
  toLg: boolean;
  hasTableView?: boolean;
  hasTabFilters?: boolean;
  hasSearch?: boolean;
  tableRowSwitch?: (rowData: IntegrationsRow) => Promise<void>;
}

const MobileTable = ({
  route,
  hasDetails,
  toMd,
  toLg,
  hasTableView,
  hasTabFilters, // this implies that there are additional tabs in the page to change the route - like in files page
  hasSearch = true,
  tableRowSwitch
}: IMobileTableProps) => {
  const CARD_ROW_HEIGHT = 56;

  const virtualize = route === ROUTES.Companies;

  const { loading, tableRows, rowSwitch, columns, inlineActions } =
    useTableStoreByRoute(
      route,
      useShallow(state => ({
        loading: state.loading,
        tableRows: state.data,
        rowSwitch: state.rowSwitch,
        columns: state.columns,
        inlineActions: state.inlineActions
      }))
    );

  const mainBannerHeight = useMainBannerStore(state => state.height);
  const filtersHeightValue = useFiltersHeightStore(state => state.height);
  const isTableHeadActive = useTableHeadStore(state => state.tableHeadActive);

  const { mobileTableItemBox } = useTableStyles();

  const { DESKTOP_TABLE_FOOTER_HEIGHT, TABLE_TOOLBAR_HEIGHT } =
    useGeneralHeights(mainBannerHeight);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [listOffsetTop, setListOffsetTop] = useState(0);

  useLayoutEffect(() => {
    if (!virtualize) return;

    const offsetTop = listRef.current?.offsetTop ?? 0;
    setListOffsetTop(current => (current === offsetTop ? current : offsetTop));
  });

  const activeColumnsCount = columns.filter(column => column.isActive).length;
  const hasActionsRow = inlineActions.length > 0 || hasDetails;

  const estimatedCardHeight =
    (activeColumnsCount + (hasActionsRow ? 1 : 0)) * CARD_ROW_HEIGHT;

  const virtualizer = useVirtualizer({
    count: tableRows?.length ?? 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedCardHeight,
    overscan: 5,
    gap: 8,
    scrollMargin: listOffsetTop,
    enabled: virtualize,
    getItemKey: rowIndex => tableRows?.[rowIndex]?.id ?? rowIndex
  });

  const renderItem = (row: TableRowData, rowIndex: number) => (
    <MobileTableItem
      route={route}
      row={row}
      isTableHeadActive={Boolean(isTableHeadActive)}
      index={rowIndex}
      isFirstItem={rowIndex === 0}
      isLastItem={rowIndex === tableRows.length - 1}
      hasDetails={hasDetails}
      tableRowSwitch={tableRowSwitch}
      rowSwitch={rowSwitch}
    />
  );

  const renderItems = () => {
    if (!virtualize) {
      return tableRows?.map((row: TableRowData, rowIndex: number) => (
        <Fragment key={row.id}>{renderItem(row, rowIndex)}</Fragment>
      ));
    }

    return virtualizer.getVirtualItems().map(virtualItem => (
      <div
        key={virtualItem.key}
        ref={virtualizer.measureElement}
        data-index={virtualItem.index}
      >
        {renderItem(tableRows[virtualItem.index], virtualItem.index)}
      </div>
    ));
  };

  const virtualItems = virtualize ? virtualizer.getVirtualItems() : [];

  const paddingTop = virtualItems.length
    ? virtualItems[0].start - listOffsetTop
    : 0;

  const paddingBottom = virtualItems.length
    ? virtualizer.getTotalSize() -
      (virtualItems[virtualItems.length - 1].end - listOffsetTop)
    : 0;

  return (
    <div
      ref={scrollContainerRef}
      data-testid="mobile-table"
      css={css({
        position: "absolute",
        top: TABLE_TOOLBAR_HEIGHT(
          toMd,
          hasSearch,
          hasTableView,
          filtersHeightValue,
          hasTabFilters
        ),
        left: "0",
        right: "0",
        bottom: DESKTOP_TABLE_FOOTER_HEIGHT(toLg),
        overflow: "auto"
      })}
    >
      <MobileTableHead route={route} />
      <div
        ref={listRef}
        css={css([
          {
            opacity: loading ? "0.4" : "1",
            transition: "opacity 300ms ease-out"
          },
          mobileTableItemBox
        ])}
        style={virtualize ? { paddingTop, paddingBottom } : undefined}
      >
        {renderItems()}
      </div>
    </div>
  );
};

export default React.memo(MobileTable);
